import React, { useCallback, useMemo } from 'react';
import {
  AbsenceFilesPlaceholderIcon,
  Box,
  CalendarPickerIcon,
  DateRangeDropdown,
  List,
  NoSearchItemsPlaceholder,
  PaginationBar,
  SearchInput,
  SearchPlaceholderIcon,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import dropdownStateAllToggling, {
  checkIsAllOptionSelected,
} from '@/utils/handlers/dropdownStateAllToggling';

import { useSearchParams } from './hooks';
import { useResendListQuery } from './queries';
import ResendListItem from './ResendListItem';

interface Props {
  employeeId: string;
}

const ResendList: React.FC<Props> = ({ employeeId }) => {
  const {
    page,
    perPage,
    searchString,
    currentSearch,
    setSearchString,
    setPerPage,
    setPage,

    statusOptions,
    setStatuses,
    statuses,

    currentTypes,
    setCurrentTypes,
    typeOptions,

    period,
    setPeriod,
  } = useSearchParams();
  const statusValues = useMemo(() => dropdownStateAllToggling(statuses), [statuses]);
  const typeValues = useMemo(() => dropdownStateAllToggling(currentTypes), [currentTypes]);
  const isAllStatusesSelected = useMemo(() => checkIsAllOptionSelected(statuses), [statuses]);
  const { data, total, isLoading } = useResendListQuery({
    page,
    perPage,
    employeeId,
    search: currentSearch,
    communicationTypes: currentTypes,
    notificationStatuses: statuses,
    startDate: period[0],
    endDate: period[1],
  });
  const areFiltersUsed = useMemo(
    () => currentSearch || !isAllStatusesSelected,
    [currentSearch, isAllStatusesSelected]
  );
  const handleResendCallback = useCallback(() => {
    setPage(0);
  }, [setPage]);

  return (
    <Box data-testId="resend-list" pad="l">
      <Box margin={{ bottom: 'l' }} direction="row" align="center" justify="between">
        <Box>
          <Text weight="bold" color="textBody" size="large" style={{ whiteSpace: 'nowrap' }}>
            Communications: <Text data-testid="resend-list_total">{total}</Text>
          </Text>
        </Box>
        <Box direction="row" justify="end">
          <Box width="220px">
            <SearchInput
              testId="statement"
              value={searchString}
              onChange={setSearchString}
              placeholder="Search"
              hasSearchingHistory
            />
          </Box>
          <Box margin={{ left: 's' }} width="160px">
            <SelectDropdown
              id="communication_type"
              name="Type"
              prefix="Type"
              testId="resend_list_type"
              values={typeValues}
              onChangeValues={setCurrentTypes}
              options={typeOptions}
              activeTitle
              // allMode
              ellipsisMode
            />
          </Box>
          <Box margin={{ left: 's' }} width="160px">
            <SelectDropdown
              id="communication_status"
              name="Status"
              prefix="Status"
              testId="resend_list_status"
              values={statusValues}
              onChangeValues={setStatuses}
              options={statusOptions}
              activeTitle
              // allMode
              ellipsisMode
            />
          </Box>

          <Box margin={{ left: 's' }} width="150px">
            <DateRangeDropdown
              prefix="Period:"
              onChange={(range) => {
                setPeriod(range);
              }}
              ellipsisMode
              startDate={period[0]}
              endDate={period[1]}
              icon={<CalendarPickerIcon color="iconPrimary" />}
            />
          </Box>
        </Box>
      </Box>
      <List>
        <List.Header>
          <Box style={{ width: '200px' }}>
            <Text color="textBody" size="small" weight="bold">
              Date
            </Text>
          </Box>
          <List.Header.Item flex={2} margin={{ left: 's' }}>
            Subject
          </List.Header.Item>
          <List.Header.Item flex={2}>Sent to</List.Header.Item>
          <List.Header.Item flex={1}>Type</List.Header.Item>
          <List.Header.Item flex={1.5}>Status</List.Header.Item>
          <Box width="24px" margin={{ left: 's' }} />
        </List.Header>
      </List>
      {isLoading ? (
        <List.Preloader testId="resend-list_preloader" count={3} />
      ) : (
        <>
          {!data.length && (
            <NoSearchItemsPlaceholder
              placeholderTitle={areFiltersUsed ? 'No results have been found' : 'There are no files yet'}
              node={
                areFiltersUsed ? (
                  <SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />
                ) : (
                  <AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />
                )
              }
              description={
                areFiltersUsed ? "Try to adjust your search or filter to find what you're looking for." : ''
              }
            />
          )}
          {data.map((item) => (
            <ResendListItem key={item.id} {...item} onResendCallback={handleResendCallback} />
          ))}
        </>
      )}
      <PaginationBar
        page={page}
        total={total}
        pageSize={perPage}
        onChangePage={setPage}
        onChangePageSize={setPerPage}
      />
    </Box>
  );
};

export default ResendList;
